import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { userAPI } from "../lib/auth";
import { useAuth } from "../contexts/AuthContext";
import { MODULES, getEffectivePermissions } from "../lib/permissions";
import { UserPermissionsDialog } from "../components/UserPermissionsDialog";
import { formatDateTime } from "../lib/utils";
import { Plus, User, Mail, Shield, ShieldCheck, Trash2, Eye, EyeOff, Pencil, KeyRound, Crown } from "lucide-react";
import { toast } from "sonner";

const USER_ROLES = [
  { value: "admin", label: "Admin", icon: ShieldCheck, color: "bg-red-100 text-red-800" },
  { value: "staff", label: "Staff", icon: Shield, color: "bg-blue-100 text-blue-800" }
];

const getModuleAccess = (user) => {
  const perms = getEffectivePermissions(user);
  const viewEdit = [];
  const viewOnly = [];
  const noAccess = [];
  MODULES.forEach(({ key, label }) => {
    const level = perms[key] || "none";
    if (level === "edit") viewEdit.push(label);
    else if (level === "view") viewOnly.push(label);
    else noAccess.push(label);
  });
  return { viewEdit, viewOnly, noAccess };
};

export function Users() {
  const { register, hasModule, canEditModule } = useAuth();
  const canEditUsers = canEditModule("users");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    full_name: "",
    password: "",
    role: "staff"
  });

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAll();
      setUsers(response);
    } catch (error) {
      toast.error("Failed to load users");
      console.error("Load users error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasModule("users")) {
      loadUsers();
    } else {
      setLoading(false);
    }
  }, [loadUsers]); // eslint-disable-line

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.username.trim() || !newUser.email.trim() || !newUser.full_name.trim() || !newUser.password.trim()) {
      toast.error("All fields are required");
      return;
    }

    const result = await register(newUser);
    if (result.success) {
      setShowCreateDialog(false);
      setNewUser({
        username: "",
        email: "",
        full_name: "",
        password: "",
        role: "staff"
      });
      loadUsers();
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }

    try {
      await userAPI.delete(userId);
      toast.success("User deleted successfully");
      loadUsers();
    } catch (error) {
      toast.error("Failed to delete user");
      console.error("Delete user error:", error);
    }
  };

  const getRoleInfo = (role) => {
    return USER_ROLES.find(r => r.value === role) || USER_ROLES[1];
  };

  if (!hasModule("users")) {
    return (
      <div className="text-center py-16">
        <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Access Denied</h2>
        <p className="text-slate-600">You need admin privileges to access user management.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="users-title">
            User Management
          </h1>
          <p className="text-slate-600 mt-1">Manage system users and their permissions</p>
        </div>
        
        {canEditUsers && (
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="btn-hover" data-testid="add-user-button">
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
              <DialogDescription>
                Create a new user account with appropriate permissions.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4" data-testid="create-user-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="username">Username *</Label>
                  <Input
                    id="username"
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    placeholder="johndoe"
                    required
                    data-testid="user-username-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="john@example.com"
                    required
                    data-testid="user-email-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  placeholder="John Doe"
                  required
                  data-testid="user-fullname-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Secure password"
                    required
                    data-testid="user-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="role">Role *</Label>
                  <Select
                    value={newUser.role}
                    onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {USER_ROLES.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex space-x-2">
                <Button type="submit" className="flex-1" data-testid="save-user-button">
                  Create User
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateDialog(false)}
                  data-testid="cancel-user-button"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Users List */}
      {users.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="users-grid">
          {users.map((user) => {
            const roleInfo = getRoleInfo(user.role);
            const RoleIcon = roleInfo.icon;
            const moduleAccess = getModuleAccess(user);
            
            return (
              <Card key={user.id} className="card-hover" data-testid={`user-card-${user.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-slate-500 to-slate-700 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                        {user.full_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg truncate">{user.full_name}</CardTitle>
                        <CardDescription className="truncate">@{user.username}</CardDescription>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={roleInfo.color}>
                        <RoleIcon className="w-3 h-3 mr-1" />
                        {roleInfo.label}
                      </Badge>
                      {user.is_super_admin && (
                        <Badge className="bg-purple-100 text-purple-800" data-testid={`super-admin-badge-${user.id}`}>
                          <Crown className="w-3 h-3 mr-1" />
                          Super Admin
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center text-sm text-slate-600">
                      <Mail className="w-4 h-4 mr-2 text-slate-400" />
                      <span className="truncate">{user.email}</span>
                    </div>
                    
                    <div className="flex items-center text-sm text-slate-600">
                      <User className="w-4 h-4 mr-2 text-slate-400" />
                      <span>{user.is_active ? "Active" : "Inactive"}</span>
                    </div>
                    
                    {user.is_super_admin ? (
                      <div className="pt-2 border-t" data-testid={`user-module-access-${user.id}`}>
                        <p className="text-xs font-semibold text-purple-700 flex items-center">
                          <Crown className="w-3 h-3 mr-1" />
                          Full access to all modules — permissions cannot be restricted
                        </p>
                      </div>
                    ) : (
                    <div className="pt-2 border-t" data-testid={`user-module-access-${user.id}`}>
                      <p className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center">
                        <Pencil className="w-3 h-3 mr-1 text-emerald-600" />
                        Can view &amp; edit
                      </p>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {moduleAccess.viewEdit.map((m) => (
                          <span key={m} className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                            {m}
                          </span>
                        ))}
                      </div>
                      {moduleAccess.viewOnly.length > 0 && (
                        <>
                          <p className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center">
                            <Eye className="w-3 h-3 mr-1 text-blue-600" />
                            View only
                          </p>
                          <div className="flex flex-wrap gap-1 mb-2">
                            {moduleAccess.viewOnly.map((m) => (
                              <span key={m} className="text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                                {m}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                      {moduleAccess.noAccess.length > 0 && (
                        <>
                          <p className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center">
                            <EyeOff className="w-3 h-3 mr-1 text-slate-400" />
                            No access
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {moduleAccess.noAccess.map((m) => (
                              <span key={m} className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
                                {m}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                    )}

                    <div className="text-xs text-slate-500 pt-2 border-t">
                      Created: {formatDateTime(user.created_date)}
                    </div>
                    
                    {canEditUsers && !user.is_super_admin && (
                      <div className="pt-2 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPermUser(user)}
                          className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`edit-permissions-${user.id}`}
                        >
                          <KeyRound className="w-4 h-4 mr-2" />
                          Permissions
                        </Button>
                        {user.username !== "admin" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id, user.username)}
                            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-16">
            <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2" data-testid="no-users-title">
              No users found
            </h3>
            <p className="text-slate-600 mb-6">
              Get started by creating your first user account
            </p>
            <Button onClick={() => setShowCreateDialog(true)} data-testid="add-first-user-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First User
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Role Information */}
      <Card>
        <CardHeader>
          <CardTitle>User Roles & Permissions</CardTitle>
          <CardDescription>
            Roles set the default access. Use the "Permissions" button on any user card to customise module access per user (None / View / Edit).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Admin</h4>
                  <p className="text-sm text-slate-600">Full system access</p>
                </div>
              </div>
              <ul className="text-sm text-slate-600 ml-11 space-y-1">
                <li>• Full access to all modules</li>
                <li>• Create and manage users</li>
                <li>• Client Billing, Projects, Reports</li>
                <li>• SMTP Settings configuration</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">Staff</h4>
                  <p className="text-sm text-slate-600">Limited edit access</p>
                </div>
              </div>
              <ul className="text-sm text-slate-600 ml-11 space-y-1">
                <li>• Manage customers, interactions, assets</li>
                <li>• Support tickets, toner, OTS KB access</li>
                <li>• No Client Billing, Projects or Reports</li>
                <li>• No user management or SMTP access</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <UserPermissionsDialog
        user={permUser}
        open={!!permUser}
        onOpenChange={(open) => { if (!open) setPermUser(null); }}
        onSaved={loadUsers}
      />
    </div>
  );
}